<template>
  <div class="container is-fluid" ref="container">
    <section class="section">
      <h1 class="title">{{ $t("mp:customer_limit.list.title") }}</h1>

      <FieldListing
        :data="list"
        :columns="tableColumns"
        :actions="actions"
        :searchable="true"
        :paginated="true"
        default-sort="created"
        store-fetch-command="customerLimit/fetchList"
        store-get-command="customerLimit/list"
        :search-fields="searchFields"
        ref="dataTable"
      >
        <b-button
          :label="$t('mp:customer_limit.create_modal.button.label.open')"
          type="is-blue"
          @click="isCreateModalActive = true"
        />
        <b-modal
          v-model="isCreateModalActive"
          has-modal-card
          trap-focus
          aria-role="dialog"
          close-button-aria-label="Close"
          :can-cancel="['escape', 'outside']"
          aria-modal
          @close="onModalClose"
        >
          <template #default="props">
            <div class="modal-card" style="width: auto">
              <header class="modal-card-head">
                <p class="modal-card-title m-0">
                  {{ $t("mp:customer_limit.create_modal.title") }}
                </p>
                <button type="button" class="delete" @click="props.close" />
              </header>
              <section class="modal-card-body">
                <ValidationObserver ref="validationObserver">
                  <ValidationProvider
                    name="customerLimitEmail"
                    rules="required"
                    v-slot="{ errors, validated, valid }"
                  >
                    <b-field
                      :label="
                        $t('mp:customer_limit.create_modal.input.label.email')
                      "
                      label-position="on-border"
                      class="mt-5 mb-6"
                      :type="{
                        'is-danger': errors[0],
                        'is-success': validated && valid,
                      }"
                      :message="errors"
                    >
                      <b-input v-model="email" required />
                    </b-field>
                  </ValidationProvider>
                  <PhoneInput
                    v-model="phone"
                    :label="
                      $t('mp:customer_limit.create_modal.input.label.phone')
                    "
                    validationProviderName="customerLimitPhone"
                  />
                </ValidationObserver>
              </section>
              <footer class="modal-card-foot">
                <b-button
                  :label="
                    $t('mp:customer_limit.create_modal.button.label.cancel')
                  "
                  class="mr-auto"
                  @click="props.close"
                />
                <b-button
                  :label="
                    $t('mp:customer_limit.create_modal.button.label.create')
                  "
                  type="is-success"
                  @click="createCustomerLimit"
                />
              </footer>
            </div>
          </template>
        </b-modal>
      </FieldListing>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { AUTHORITIES } from "@/utils/authorities";
import { SEARCH_FIELD_TYPES, CUSTOMER_LIMIT_STATUS_TOKENS } from "@/utils/const";
import { localizedFormatter, applyCustomStringMask } from "@/utils/util";
import exportMixin from "@/mixins/exportMixin";
import PhoneInput from "../components/input/PhoneInput.vue";

export default {
  name: "CustomerLimits",
  title: "mp:page.customer_limit.list.title",
  mixins: [exportMixin],
  components: {
    FieldListing: () => import("@/components/FieldListing.vue"),
    PhoneInput,
  },
  computed: {
    ...mapGetters({
      fileGetter: "drawer/getFile",
      country: "i18n/country",
      locale: "i18n/locale",
      lastNameFirst: "i18n/lastNameFirst",
      phoneMask: "i18n/phoneMask",
      list: "customerLimit/list",
    }),
    canSeebnplApplicationsExport() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.BNPL.APPLICATION.ADMIN,
        AUTHORITIES.BNPL.APPLICATION.EXPORT,
      ]);
    },
    isModalValid() {
      return this.$refs.validationObserver.flags.valid;
    },
  },
  data() {
    return {
      isCreateModalActive: false,
      email: "",
      phone: "",
      searchFields: [
        {
          name: "mp:customer_limit.list_header.created",
          identifier: "created",
          type: SEARCH_FIELD_TYPES.DATE,
          genericSearch: true,
        },
        {
          name: "mp:customer_limit.list_header.limit_status",
          identifier: "limitStatus",
          type: SEARCH_FIELD_TYPES.ENUM,
          values: CUSTOMER_LIMIT_STATUS_TOKENS,
          genericSearch: true,
        },
        {
          name: "mp:customer_limit.list_header.expiration_date",
          identifier: "expirationDate",
          type: SEARCH_FIELD_TYPES.DATE,
          genericSearch: true,
        },
      ],
      tableColumns: [
        {
          field: "name",
          label: "mp:customer_limit.list_header.name",
          sortable: false,
          format: (row) => {
            if (!row.lastName && !row.firstName) {
              return '-'
            } else {
              if (this.lastNameFirst) {
                return `${row.lastName ?? ''} ${row.firstName ?? ''}`;
              } else {
                return `${row.firstName ?? ''} ${row.lastName ?? ''}`;
              }
            }
          },
        },
        {
          field: "email",
          label: "mp:customer_limit.list_header.email",
          sortable: true,
          format: (row) => {
            return row.email ?? '-';
          },
        },
        {
          field: "phone",
          label: "mp:customer_limit.list_header.phone",
          sortable: true,
          format: (row) => {
            return row.phone ? applyCustomStringMask(this.phoneMask, row.phone) : '-';
          },
        },
        {
          field: "created",
          label: "mp:customer_limit.list_header.created",
          sortable: true,
          format: (row) => {
            return localizedFormatter(this.locale).date(row.created);
          },
        },
        {
          field: "limitStatus",
          label: "mp:customer_limit.list_header.limit_status",
          sortable: true,
          format: (row) => {
            return row.limitStatus;
          },
        },
        {
          field: "currentLimit",
          label: "mp:customer_limit.list_header.current_limit",
          sortable: true,
          format: (row) => {
            return localizedFormatter(this.locale).money(
              "HUF",
              row.currentLimit > 0 ? row.currentLimit : 0
            );
          },
        },
        {
          field: "expirationDate",
          label: "mp:customer_limit.list_header.expiration_date",
          sortable: true,
          format: (row) => {
            if (!row.expirationDate) {
              return '-';
            }
            return localizedFormatter(this.locale).date(row.expirationDate);
          },
        },
      ],
      actions: [
        {
          icon: "chevron-right",
          iconPack: "fa",
          type: "is-grey",
          function: (row) => {
            this.$router.push({
              name: "CustomerLimit",
              params: {
                limitId: row.id,
              },
            });
          },
        },
      ],
    };
  },
  methods: {
    ...mapActions({
      fetchFile: "drawer/download",
      fetchList: "customerLimit/fetchList",
      createDraft: "customerLimit/createDraft",
    }),
    onModalClose() {
      this.email = "";
      this.phone = "";
    },
    async createCustomerLimit() {
      if (this.isModalValid) {
        try {
          await this.createDraft({ phone: this.phone, email: this.email });
          this.isCreateModalActive = false;
          this.fetchList("sort=created,desc");
          this.$buefy.toast.open({
            message: this.$i18n.t(
              "mp:customer_limit.create_modal.toast.success"
            ),
            type: "is-success",
          });
        } catch {
          this.$buefy.toast.open({
            message: this.$i18n.t("mp:customer_limit.create_modal.toast.fail"),
            type: "is-danger",
          });
        }
      } else {
        this.$buefy.toast.open({
          message: this.$i18n.t("mp:customer_limit.create_modal.toast.fail"),
          type: "is-danger",
        });
      }
    },
  },
};
</script>

<style scoped></style>
